import { QueryClient } from '@tanstack/react-query';
import { cachePresets } from './cachePresets';

/**
 * Create a tenant-safe QueryClient instance
 * 
 * ## Default Configuration
 * 
 * Uses `cachePresets.stable` as the default (5 min staleTime).
 * Individual hooks should override with appropriate presets:
 * 
 * - `cachePresets.realTime` - Availability, live status (staleTime: 0)
 * - `cachePresets.frequent` - Validation, active sessions (staleTime: 1 min)
 * - `cachePresets.stable` - User profile, club details (staleTime: 5 min)
 * - `cachePresets.staticData` - Payment settings, terms (staleTime: 15 min)
 * 
 * See `~/lib/query/cachePresets.ts` for full documentation.
 * 
 * ## Security Notes
 * 
 * - Queries are invalidated when tenant/club changes to prevent data leaks
 * - Cache keys include scope identifiers (clubId or tenantHandle)
 * - URL change invalidation plugin handles automatic cache clearing
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default to "stable" preset - hooks override as needed
        staleTime: cachePresets.stable.staleTime,
        gcTime: cachePresets.stable.gcTime,

        // Retry failed requests once
        retry: 1,

        // Don't refetch on window focus by default
        // Real-time hooks opt-in via cachePresets.realTime
        refetchOnWindowFocus: false,

        // Use React Query default for refetchOnMount (true)
        // This ensures stale data is refetched when components remount
        // Hooks that need different behavior can override explicitly
      },
      mutations: {
        retry: 1,
      },
    },
  });
}
